import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useDashboardStore from '../store/dashboardStore';

const FAQ_ITEMS = [
  {
    q: 'What happens when I hit my scan or API limit?',
    a: 'You can continue at a small per-unit overage fee, so your workflows are never interrupted. You will receive alerts at 80% and 100% usage.',
  },
  {
    q: 'Can I switch plans at any time?',
    a: 'Yes. Upgrades take effect immediately and you are only charged the prorated difference. Downgrades take effect at the start of your next billing cycle.',
  },
  {
    q: 'Is there a contract or commitment?',
    a: 'No. All plans are month-to-month with no long-term contracts. Annual billing gives you a 20% discount but you can cancel at any time.',
  },
  {
    q: 'What payment methods do you accept?',
    a: 'We accept all major credit cards (Visa, Mastercard, Amex) through Stripe. Enterprise customers can also pay via invoice or ACH transfer.',
  },
  {
    q: 'What is the 14-day money-back guarantee?',
    a: 'If you are not satisfied within the first 14 days of any paid plan, contact us for a full refund. No questions asked.',
  },
  {
    q: 'What does "All Apps Included" mean?',
    a: 'Professional and Enterprise plans include access to every app in the Quantrolix ecosystem -- Fire POS, Tea Effects, PhantomCast, Menu Mentor, and more -- at no extra cost.',
  },
];

const COMPARISON_FEATURES = [
  { label: 'Product Scans / mo', key: 'scans', format: (v) => v === -1 ? 'Unlimited' : v.toLocaleString() },
  { label: 'API Calls / mo', key: 'api_calls', format: (v) => v === -1 ? 'Unlimited' : v.toLocaleString() },
  { label: 'Marketplaces', key: 'marketplaces', format: (v) => v >= 100 ? 'All + Beta' : v >= 83 ? 'All 83+' : `${v}` },
  { label: 'App Access', key: 'apps', format: (v) => v === -1 ? 'All Apps' : `${v} App${v !== 1 ? 's' : ''}` },
  { label: 'Team Members', key: 'team_members', format: (v) => v === -1 ? 'Unlimited' : `${v}` },
  { label: 'Data Retention', key: 'data_retention_days', format: (v) => v === -1 ? 'Unlimited' : v >= 365 ? `${Math.round(v / 365)} Year${v >= 730 ? 's' : ''}` : `${v} Days` },
  { label: 'Real-Time Alerts', key: null, tiers: { starter: true, professional: true, enterprise: true } },
  { label: 'Advanced Analytics', key: null, tiers: { starter: false, professional: true, enterprise: true } },
  { label: 'API Access', key: null, tiers: { starter: false, professional: true, enterprise: true } },
  { label: 'Custom Dashboards', key: null, tiers: { starter: false, professional: true, enterprise: true } },
  { label: 'Priority Support', key: null, tiers: { starter: false, professional: true, enterprise: true } },
  { label: 'Dedicated Account Mgr', key: null, tiers: { starter: false, professional: false, enterprise: true } },
  { label: 'Custom Integrations', key: null, tiers: { starter: false, professional: false, enterprise: true } },
  { label: 'SLA Guarantee', key: null, tiers: { starter: false, professional: false, enterprise: true } },
  { label: 'SSO / SAML', key: null, tiers: { starter: false, professional: false, enterprise: true } },
];

const PricingModal = ({ onClose, onNotification }) => {
  const { currentUser, getPlanConfig, planDisplayOrder } = useDashboardStore((state) => ({
    currentUser: state.currentUser,
    getPlanConfig: state.getPlanConfig,
    planDisplayOrder: state.planDisplayOrder,
  }));

  const [openFaq, setOpenFaq] = useState(null);
  const [isAnnual, setIsAnnual] = useState(false);

  const handleUpgrade = async (planKey) => {
    const plan = getPlanConfig(planKey);

    if (planKey === 'enterprise') {
      onNotification('Please contact sales@quantrolix.ai for Enterprise pricing', 'info');
      return;
    }

    try {
      onNotification(`Creating checkout session for ${plan.name}...`, 'info');

      const token = localStorage.getItem('auth_token');
      if (!token) {
        onNotification('Please log in to upgrade your plan', 'error');
        return;
      }

      const response = await fetch('https://quantrolix-backend-v2-production.up.railway.app/api/payments/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          tier: planKey,
          billing_cycle: isAnnual ? 'annual' : 'monthly',
          success_url: `${window.location.origin}/dashboard?payment=success`,
          cancel_url: `${window.location.origin}/dashboard?payment=cancelled`,
        }),
      });

      if (!response.ok) {
        throw new Error(`Checkout failed: ${response.status}`);
      }

      const data = await response.json();

      if (data.checkout_url) {
        onNotification('Redirecting to Stripe checkout...', 'success');
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      onNotification('Failed to create checkout session. Please try again.', 'error');
    }
  };

  const plans = planDisplayOrder.map((key) => getPlanConfig(key));
  const resolvedCurrent = getPlanConfig(currentUser.plan);

  return (
    <div className="pricing-modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(10, 10, 15, 0.95)',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '40px 20px',
      overflowY: 'auto',
    }}>
      <div className="pricing-modal-content" style={{
        background: 'rgba(10, 10, 15, 0.98)',
        border: '1px solid rgba(102, 126, 234, 0.3)',
        borderRadius: '24px',
        padding: '48px 40px',
        maxWidth: '1100px',
        width: '100%',
        boxShadow: '0 0 60px rgba(102, 126, 234, 0.3)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '40px',
          gap: '16px',
        }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{
              color: '#00d4ff',
              fontSize: '2rem',
              fontFamily: 'Orbitron, monospace',
              marginBottom: '10px',
            }}>
              Choose Your Plan
            </h2>
            <p style={{
              color: 'rgba(255, 255, 255, 0.65)',
              fontSize: '1.05rem',
              maxWidth: '500px',
              lineHeight: '1.5',
            }}>
              Powerful marketplace intelligence for every stage of growth.
            </p>
            <p style={{
              color: 'rgba(255, 255, 255, 0.45)',
              fontSize: '0.85rem',
              marginTop: '8px',
            }}>
              14-day money-back guarantee on all plans.{' '}
              <Link to="/legal" style={{ color: '#00d4ff', textDecoration: 'underline' }} onClick={onClose}>Terms</Link>
              {' & '}
              <Link to="/legal#privacy" style={{ color: '#00d4ff', textDecoration: 'underline' }} onClick={onClose}>Privacy</Link>
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close pricing modal"
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '2rem',
              cursor: 'pointer',
              lineHeight: 1,
              padding: '4px 8px',
              minWidth: '44px',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {'\u00d7'}
          </button>
        </div>

        {/* Annual / Monthly Toggle */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          marginBottom: '40px',
          flexWrap: 'wrap',
        }}>
          <span style={{
            color: !isAnnual ? '#00d4ff' : 'rgba(255, 255, 255, 0.5)',
            fontSize: '0.95rem',
            fontWeight: !isAnnual ? '600' : '400',
            transition: 'color 0.2s',
          }}>
            Monthly
          </span>
          <button
            onClick={() => setIsAnnual(!isAnnual)}
            aria-label={isAnnual ? 'Switch to monthly billing' : 'Switch to annual billing'}
            style={{
              position: 'relative',
              width: '56px',
              height: '30px',
              borderRadius: '15px',
              border: 'none',
              background: isAnnual
                ? 'linear-gradient(135deg, #9333ea, #667eea)'
                : 'rgba(255, 255, 255, 0.15)',
              cursor: 'pointer',
              transition: 'background 0.3s',
              padding: 0,
              minWidth: '56px',
            }}
          >
            <div style={{
              position: 'absolute',
              top: '3px',
              left: isAnnual ? '29px' : '3px',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: 'white',
              transition: 'left 0.3s',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            }} />
          </button>
          <span style={{
            color: isAnnual ? '#00d4ff' : 'rgba(255, 255, 255, 0.5)',
            fontSize: '0.95rem',
            fontWeight: isAnnual ? '600' : '400',
            transition: 'color 0.2s',
          }}>
            Annual
          </span>
          {isAnnual && (
            <span style={{
              background: 'linear-gradient(135deg, #22c55e, #16a34a)',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '20px',
              fontSize: '0.8rem',
              fontWeight: 'bold',
            }}>
              Save 20%
            </span>
          )}
        </div>

        {/* Plan Cards */}
        <div className="pricing-modal-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '24px',
          marginBottom: '60px',
        }}>
          {plans.map((plan) => {
            const isCurrent = resolvedCurrent?.key === plan.key;
            const isPopular = Boolean(plan.is_popular);
            const isEnterprise = plan.key === 'enterprise';
            const displayPrice = isAnnual ? plan.annual_price : plan.price;

            return (
              <div
                key={plan.key}
                style={{
                  background: isCurrent
                    ? 'rgba(102, 126, 234, 0.15)'
                    : isPopular
                      ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.15), rgba(102, 126, 234, 0.15))'
                      : 'rgba(255, 255, 255, 0.02)',
                  border: `2px solid ${isCurrent ? '#667eea' : isPopular ? '#9333ea' : 'rgba(102, 126, 234, 0.2)'}`,
                  borderRadius: '20px',
                  padding: '32px 28px',
                  position: 'relative',
                  transform: isPopular ? 'scale(1.04)' : 'scale(1)',
                  zIndex: isPopular ? 2 : 1,
                  transition: 'transform 0.2s, border-color 0.2s',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onMouseEnter={(e) => {
                  if (!isPopular) e.currentTarget.style.transform = 'translateY(-6px)';
                }}
                onMouseLeave={(e) => {
                  if (!isPopular) e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {isPopular && (
                  <div style={{
                    position: 'absolute',
                    top: '-14px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'linear-gradient(135deg, #9333ea, #667eea)',
                    color: 'white',
                    padding: '6px 18px',
                    borderRadius: '20px',
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    letterSpacing: '0.5px',
                    whiteSpace: 'nowrap',
                  }}>
                    MOST POPULAR
                  </div>
                )}

                {isCurrent && !isPopular && (
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#667eea',
                    color: 'white',
                    padding: '4px 14px',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                  }}>
                    CURRENT PLAN
                  </div>
                )}

                <h3 style={{
                  color: isPopular ? '#9333ea' : '#00d4ff',
                  fontFamily: 'Orbitron, monospace',
                  fontSize: '1.2rem',
                  marginBottom: '6px',
                  marginTop: (isPopular || isCurrent) ? '8px' : '0',
                }}>
                  {plan.name}
                </h3>

                <p style={{
                  color: 'rgba(255, 255, 255, 0.55)',
                  fontSize: '0.85rem',
                  lineHeight: '1.4',
                  minHeight: '40px',
                  marginBottom: '16px',
                }}>
                  {plan.description}
                </p>

                <div style={{
                  fontSize: '2.4rem',
                  fontWeight: 'bold',
                  marginBottom: '4px',
                  color: 'white',
                }}>
                  ${displayPrice}
                  <span style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.5)', fontWeight: '400' }}>/mo</span>
                </div>

                {isAnnual && (
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.4)',
                    fontSize: '0.8rem',
                    marginBottom: '16px',
                  }}>
                    Billed annually (${displayPrice * 12}/yr)
                  </p>
                )}

                {!isAnnual && (
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.4)',
                    fontSize: '0.8rem',
                    marginBottom: '16px',
                  }}>
                    Billed monthly
                  </p>
                )}

                {/* Feature list */}
                <div style={{
                  borderTop: '1px solid rgba(102, 126, 234, 0.15)',
                  paddingTop: '16px',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  fontSize: '0.88rem',
                  marginBottom: '20px',
                }}>
                  {(plan.features || []).slice(0, 8).map((feature, idx) => (
                    <div key={idx} style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                      <span style={{ color: isPopular ? '#9333ea' : '#00d4ff', marginRight: '8px' }}>&#10003;</span>
                      {feature}
                    </div>
                  ))}
                  {(plan.features || []).length > 8 && (
                    <div style={{ color: 'rgba(255, 255, 255, 0.45)', fontSize: '0.82rem' }}>
                      +{plan.features.length - 8} more features
                    </div>
                  )}
                </div>

                {/* CTA */}
                {!isCurrent ? (
                  <button
                    onClick={() => handleUpgrade(plan.key)}
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: isPopular
                        ? 'linear-gradient(135deg, #9333ea, #667eea)'
                        : 'linear-gradient(135deg, #667eea, #00d4ff)',
                      border: 'none',
                      borderRadius: '10px',
                      color: 'white',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      transition: 'opacity 0.2s',
                      minHeight: '48px',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                  >
                    {plan.cta || 'Upgrade Now'}
                  </button>
                ) : (
                  <div style={{
                    width: '100%',
                    padding: '14px',
                    background: 'rgba(102, 126, 234, 0.15)',
                    border: '1px solid rgba(102, 126, 234, 0.3)',
                    borderRadius: '10px',
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontWeight: '600',
                    textAlign: 'center',
                    fontSize: '0.95rem',
                    minHeight: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    Current Plan
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Feature Comparison Table */}
        <div style={{ marginBottom: '60px' }}>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            marginBottom: '24px',
            textAlign: 'center',
            color: 'white',
          }}>
            Feature Comparison
          </h3>
          <div style={{
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            border: '1px solid rgba(102, 126, 234, 0.2)',
            borderRadius: '16px',
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.9rem',
              minWidth: '500px',
            }}>
              <thead>
                <tr style={{ background: 'rgba(102, 126, 234, 0.1)' }}>
                  <th style={{
                    padding: '14px 20px',
                    textAlign: 'left',
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontWeight: '500',
                    borderBottom: '1px solid rgba(102, 126, 234, 0.2)',
                    minWidth: '180px',
                  }}>
                    Feature
                  </th>
                  {plans.map((plan) => (
                    <th key={plan.key} style={{
                      padding: '14px 16px',
                      textAlign: 'center',
                      color: plan.is_popular ? '#9333ea' : '#00d4ff',
                      fontWeight: '600',
                      fontFamily: 'Orbitron, monospace',
                      fontSize: '0.85rem',
                      borderBottom: '1px solid rgba(102, 126, 234, 0.2)',
                      whiteSpace: 'nowrap',
                    }}>
                      {plan.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON_FEATURES.map((row, idx) => (
                  <tr
                    key={idx}
                    style={{
                      borderBottom: idx < COMPARISON_FEATURES.length - 1
                        ? '1px solid rgba(102, 126, 234, 0.1)'
                        : 'none',
                      background: idx % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.015)',
                    }}
                  >
                    <td style={{
                      padding: '12px 20px',
                      color: 'rgba(255, 255, 255, 0.8)',
                      whiteSpace: 'nowrap',
                    }}>
                      {row.label}
                    </td>
                    {plans.map((plan) => {
                      let cellContent;
                      if (row.key) {
                        cellContent = row.format(plan[row.key]);
                      } else if (row.tiers) {
                        const hasFeature = row.tiers[plan.key];
                        cellContent = hasFeature
                          ? <span style={{ color: '#22c55e', fontSize: '1.1rem' }}>&#10003;</span>
                          : <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>--</span>;
                      }
                      return (
                        <td key={plan.key} style={{
                          padding: '12px 16px',
                          textAlign: 'center',
                          color: 'rgba(255, 255, 255, 0.75)',
                        }}>
                          {cellContent}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ Section */}
        <div>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            marginBottom: '24px',
            textAlign: 'center',
            color: 'white',
          }}>
            Frequently Asked Questions
          </h3>
          <div style={{
            maxWidth: '760px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}>
            {FAQ_ITEMS.map((item, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div
                  key={idx}
                  style={{
                    border: '1px solid rgba(102, 126, 234, 0.2)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    background: isOpen ? 'rgba(102, 126, 234, 0.05)' : 'transparent',
                  }}
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    style={{
                      width: '100%',
                      padding: '16px 20px',
                      background: 'none',
                      border: 'none',
                      color: 'white',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      minHeight: '48px',
                    }}
                  >
                    <span>{item.q}</span>
                    <span style={{
                      marginLeft: '16px',
                      fontSize: '1.2rem',
                      color: '#00d4ff',
                      transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                      flexShrink: 0,
                    }}>
                      +
                    </span>
                  </button>
                  {isOpen && (
                    <div style={{
                      padding: '0 20px 16px',
                      color: 'rgba(255, 255, 255, 0.65)',
                      fontSize: '0.9rem',
                      lineHeight: '1.6',
                    }}>
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingModal;
